import { FilteredCreditApplication } from '@applyonline/shared'
import { ECreditApplicationStatus, EDecision } from '@if/core/types'
import { useMemo } from 'react'
import calculateMonthlyAmount from '../../data/compute-selectors'
import { Constants } from '../../types/constants'

export const isServerAnalysingCredit = (creditApp: FilteredCreditApplication | null): boolean => {
  return (
    creditApp !== null &&
    creditApp.status === ECreditApplicationStatus.Active &&
    ((creditApp.consentHardHit && !creditApp.normsDecision) || creditApp.prequalificationDecision === null)
  )
}

export const canPollCreditApp = (creditApp: FilteredCreditApplication | null): boolean => {
  if (creditApp) {
    let maxTime = new Date()
    const thirtySeconds = 30 * 1000
    maxTime = new Date(maxTime.getTime() - thirtySeconds)
    const creditAppTime = new Date(creditApp.updatedOn)
    return creditAppTime >= maxTime
  }
  return false
}
export const useComputeMinMaxPaymentForLoanAmount = (
  creditApp: FilteredCreditApplication,
  loanAmount: number,
  maxLoanTerm: number,
): [number, number] => {
  return useMemo(() => {
    const value = parseFloat(loanAmount.toString())
    if (value > 0 && creditApp.prequalificationDecision) {
      const minPayment = calculateMonthlyAmount(
        maxLoanTerm,
        creditApp.prequalificationDecision.minInterestRate,
        value,
        Constants.FinanceFeeRateForRegular,
      )
      const maxPayment = calculateMonthlyAmount(
        maxLoanTerm,
        creditApp.prequalificationDecision.maxInterestRate > 0
          ? creditApp.prequalificationDecision.maxInterestRate
          : Constants.MaxRate,
        value,
        Constants.FinanceFeeRateForRegular,
      )
      return [minPayment, maxPayment]
    }
    return [0, 0]
  }, [creditApp, loanAmount, maxLoanTerm])
}

export const hasDecisionningError = (creditApp: FilteredCreditApplication | null): boolean => {
  if (creditApp) {
    const isServerDecisionning = isServerAnalysingCredit(creditApp)
    const canPoll = canPollCreditApp(creditApp)

    return (isServerDecisionning && !canPoll) || creditApp.finalDecision?.decision === EDecision.Error
  }
  return false
}

export type HighLevelPage =
  | 'loading' // loading the app is loading
  | 'notFound' // id does not exists
  | 'vouched' // app created but need to validate identity
  | 'analysis-running' // server is analysing prequal or hard hit
  | 'decisionning-error' // either takes more time than normal to make a decision, has a decision error
  | 'prequal-result' // we have a prequalification result to display (no credit report, approved, declined)
  | 'hardhit-result' // we have a hard hit that yields a non approved final decision (error, declined, manual-revision, etc)
  | 'first-approval' // we have a first hard hit result to display (meaning the customer never got an approved decision and never clicked Get that loan)
  | 'approved' // the customer clicked "get that loan" and final decision is approved
  | 'funding' // the contract has been signed and approved
  | 'closed' // either cancelled or completed
  | 'cancelling' // the client wanted to cancel their app but asked an agent to contact them
  | 'multipleCreditReportsFound' // Multiple credit reports returned from equifax

export const computeHighLevelPage = (
  creditApp: FilteredCreditApplication | null,
  isFetchingCreditApp: boolean,
): HighLevelPage => {
  if (!creditApp) {
    if (isFetchingCreditApp) return 'loading'
    return 'notFound'
  }
  if (
    creditApp.status === ECreditApplicationStatus.Cancelled ||
    creditApp.status === ECreditApplicationStatus.Completed
  )
    return 'closed'
  if (creditApp.status === ECreditApplicationStatus.Draft) return 'vouched'
  if (isServerAnalysingCredit(creditApp)) return 'analysis-running'
  if (creditApp.consentHardHit === false) return 'prequal-result'
  if (creditApp.finalDecision.decision === EDecision.Approved && creditApp.worksheet === null) return 'first-approval'
  if (creditApp.loanCreated) return 'funding'
  if (creditApp.finalDecision.decision === EDecision.Approved) return 'approved'
  if (creditApp.applicant.multipleCreditReportsFound) return 'multipleCreditReportsFound'
  return 'hardhit-result'
}
